import { keyframes } from '@emotion/react';
import styled from '@emotion/styled';
import React from 'react';

import type { LensSpanProps } from '../../types';
import { getColorValue } from '../../utilities';

const ringAnimation = keyframes`
  0% {
    transform: scale(1);
    opacity: 0;
  }
  50% {
    transform: scale(1.6);
    opacity: 0.4;
  }
  100% {
    transform: scale(2);
    opacity: 0;
  }
`;

const indicatorStyles = {
  dot: {
    size: '8px',
    borderWidth: '1px',
    borderColor: getColorValue('background'),
  },
  positioning: {
    top: '-2px',
    right: '-2px',
  },
  animation: {
    duration: '2s',
    timing: 'ease-out',
    iteration: 'infinite',
  },
};

const colorStyles = {
  blue: getColorValue('blue'),
  orange: getColorValue('orange'),
};

const IndicatorContainer = styled.span`
  position: relative;
  display: inline-block;
`;

const IndicatorWrapper = styled.span`
  position: absolute;
  height: ${indicatorStyles.dot.size};
  width: ${indicatorStyles.dot.size};
  top: ${indicatorStyles.positioning.top};
  right: ${indicatorStyles.positioning.right};
`;

const IndicatorDot = styled.span<Omit<IndicatorProps, 'children'>>`
  position: absolute;
  height: 100%;
  width: 100%;
  border-radius: var(--lns-radius-full);
  background-color: ${props => colorStyles[props.color || 'blue']};
  border: ${indicatorStyles.dot.borderWidth} solid
    ${indicatorStyles.dot.borderColor};

  &::after {
    content: '';
    position: absolute;
    height: 100%;
    width: 100%;
    border-radius: var(--lns-radius-full);
    background-color: ${props => colorStyles[props.color || 'blue']};
    opacity: 0;
    animation: ${props => (props.withPulse ? ringAnimation : 'none')}
      ${indicatorStyles.animation.duration} ${indicatorStyles.animation.timing}
      ${indicatorStyles.animation.iteration};
    display: ${props => (props.withPulse ? 'block' : 'none')};
  }
`;

const Indicator = ({
  withPulse = true,
  color = 'blue',
  children,
  ...props
}: IndicatorProps &
  React.ComponentProps<typeof IndicatorContainer> &
  Omit<
    LensSpanProps,
    keyof IndicatorProps & React.ComponentProps<typeof IndicatorContainer>
  >) => {
  const dotElement = <IndicatorDot withPulse={withPulse} color={color} />;

  return (
    <IndicatorContainer {...props}>
      {children}
      <IndicatorWrapper>{dotElement}</IndicatorWrapper>
    </IndicatorContainer>
  );
};

type IndicatorProps = {
  withPulse?: boolean;
  color?: 'blue' | 'orange';
  children: React.ReactNode;
};

export default Indicator;
